/**
 * AI Agents API Service
 *
 * Service functions for interacting with the AI agents API endpoints.
 */

import { apiClient } from "./api";
import type { Agent } from "@/types/security";

const API_PREFIX = "/api/v1/ai-agents";

/**
 * Backend response type (matches AIAgentStats schema)
 */
export interface AIAgentBackend {
  id: number;
  name: string;
  description: string | null;
  trace_source: string;
  otel_scope_name: string | null;
  model_name: string;
  model_provider: string | null;
  critical_issues: number;
  high_issues: number;
  total_issues: number;
  trend_percentage: number;
  trend_direction: "up" | "down" | "flat";
  trace_count: number;
  first_seen_at: string;
  last_seen_at: string;
}

/**
 * Transform backend response to frontend Agent type
 */
function transformAgent(data: AIAgentBackend): Agent {
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    modelName: data.model_name,
    traceSource: data.trace_source,
    otelScopeName: data.otel_scope_name,
    modelProvider: data.model_provider,
    criticalIssues: data.critical_issues,
    highIssues: data.high_issues,
    totalIssues: data.total_issues,
    traceCount: data.trace_count,
    trendPercentage: data.trend_percentage,
    trendDirection: data.trend_direction,
    firstSeenAt: data.first_seen_at,
    lastSeenAt: data.last_seen_at,
  };
}

/**
 * List all AI agents with security issue statistics
 * @param days - Number of days for trend calculation (default: 30)
 */
export async function listAIAgents(days: number = 30): Promise<Agent[]> {
  const params = new URLSearchParams();
  params.append("days", days.toString());
  const url = `${API_PREFIX}${params.toString() ? `?${params.toString()}` : ""}`;
  const response = await apiClient.get<AIAgentBackend[]>(url);
  return response.map(transformAgent);
}
